/**
 * safetyAlertService.js
 * 
 * Real-time safety alert pipeline.
 * When an agent's session detects a priority trigger phrase, this service
 * immediately writes to the Firestore `safetyAlerts` collection.
 * The supervisor's dashboard subscribes to this collection and gets
 * live updates — no waiting for the note to be submitted.
 * 
 * Flow:
 *   1. Agent records session
 *   2. Speech recognition detects "suicidal", "self-harm", "abuse", etc.
 *   3. audioRecorderService fires onTriggerDetected callback
 *   4. ActiveSessionPage calls pushSafetyAlert()
 *   5. Alert written to Firestore `safetyAlerts/{alertId}`
 *   6. Admin dashboard listener fires immediately
 *   7. Supervisor sees alert with agent name, individual, trigger phrase, timestamp
 *   8. Supervisor can call agent, view live transcript, initiate crisis protocol
 */

import {
  collection, doc, addDoc, updateDoc, onSnapshot,
  query, where, orderBy, limit, serverTimestamp,
} from "firebase/firestore";
import { db, auth } from "./firebase";
import { orgCollection, orgDoc } from "./orgPaths";

// ===== PUSH ALERT (called from agent's session) =====

/**
 * Push a real-time safety alert to Firestore.
 * Called the instant a priority trigger is detected during recording.
 */
export async function pushSafetyAlert({
  triggerPhrase,
  triggerType,
  category,
  severity,
  individualName,
  individualId,
  sessionId,
  organization,
  transcriptSnippet,
  transcriptFull,
  triggerTimestamp,
  agentLocation,
}) {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.error("[Safety] Cannot push alert — not authenticated");
      return { success: false };
    }

    const org = organization || localStorage.getItem("fieldnotes_user_org") || "default-org";

    const alert = {
      // Who
      agentId: user.uid,
      agentName: user.displayName || user.email,
      agentEmail: user.email,
      individualName: individualName || "Unknown",
      individualId: individualId || null,

      // What — use provided category/severity, fall back to auto-detection
      triggerPhrase: triggerPhrase,
      triggerType: triggerType || "realtimeAlert",
      category: category || categorizeTrigger(triggerPhrase),
      severity: severity || getSeverity(triggerPhrase),

      // Context — full transcript for supervisor deep-dive
      sessionId: sessionId || null,
      transcriptSnippet: transcriptSnippet || "",
      transcriptFull: (transcriptFull || "").substring(0, 100000), // Cap at 100k chars
      triggerTimestamp: triggerTimestamp || null,
      agentLocation: agentLocation || null,

      // Org scoping
      organization: org,

      // Status
      status: "active",
      acknowledgedBy: null,
      acknowledgedAt: null,
      reviewedBy: null,
      reviewedAt: null,
      resolvedBy: null,
      resolvedAt: null,
      resolutionNotes: null,

      // Timestamps
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const alertRef = await addDoc(orgCollection("safetyAlerts"), alert);
    console.log(`[Safety] 🚨 Alert pushed: ${alertRef.id} — "${triggerPhrase}"`);

    return { success: true, id: alertRef.id };
  } catch (e) {
    console.error("[Safety] Failed to push alert:", e);
    // Queue for offline sync
    try {
      const queue = JSON.parse(localStorage.getItem("fn_safety_queue") || "[]");
      queue.push({ triggerPhrase, triggerType, individualName, individualId, sessionId, organization, transcriptSnippet, queuedAt: new Date().toISOString() });
      localStorage.setItem("fn_safety_queue", JSON.stringify(queue));
    } catch (qe) {
      console.error("[Safety] Failed to queue alert:", qe);
    }
    return { success: false, error: e.message };
  }
}


// ===== FLUSH OFFLINE SAFETY QUEUE =====

export async function flushSafetyQueue() {
  try {
    const queue = JSON.parse(localStorage.getItem("fn_safety_queue") || "[]");
    if (queue.length === 0) return;

    console.log(`[Safety] Flushing ${queue.length} queued alerts...`);
    for (const alert of queue) {
      await pushSafetyAlert(alert);
    }
    localStorage.setItem("fn_safety_queue", "[]");
  } catch (e) {
    console.error("[Safety] Failed to flush queue:", e);
  }
}


// ===== SUPERVISOR ACTIONS =====

/**
 * Supervisor acknowledges an alert (first response)
 */
export async function acknowledgeAlert(alertId, supervisorName) {
  try {
    const ref = orgDoc("safetyAlerts", alertId);
    await updateDoc(ref, {
      status: "acknowledged",
      acknowledgedBy: supervisorName,
      acknowledgedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return { success: true };
  } catch (e) {
    console.error("[Safety] Failed to acknowledge:", e);
    return { success: false };
  }
}

/**
 * Supervisor marks alert as reviewed (after reviewing context)
 */
export async function reviewAlert(alertId, supervisorName, notes = "") {
  try {
    const ref = orgDoc("safetyAlerts", alertId);
    await updateDoc(ref, {
      status: "reviewed",
      reviewedBy: supervisorName,
      reviewedAt: serverTimestamp(),
      reviewNotes: notes,
      updatedAt: serverTimestamp(),
    });
    return { success: true };
  } catch (e) {
    console.error("[Safety] Failed to review:", e);
    return { success: false };
  }
}

/**
 * Supervisor resolves an alert (crisis handled)
 */
export async function resolveAlert(alertId, supervisorName, resolutionNotes) {
  try {
    const ref = orgDoc("safetyAlerts", alertId);
    await updateDoc(ref, {
      status: "resolved",
      resolvedBy: supervisorName,
      resolvedAt: serverTimestamp(),
      resolutionNotes: resolutionNotes || "",
      updatedAt: serverTimestamp(),
    });
    return { success: true };
  } catch (e) {
    console.error("[Safety] Failed to resolve:", e);
    return { success: false };
  }
}


// ===== REAL-TIME LISTENER (for admin dashboard) =====

/**
 * Subscribe to real-time safety alerts for an organization.
 * Returns unsubscribe function.
 */
export function onSafetyAlerts(organizationId, callback) {
  const q = query(
    orgCollection("safetyAlerts", organizationId),
    orderBy("createdAt", "desc"),
    limit(50)
  );

  return onSnapshot(q, (snapshot) => {
    const alerts = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(alerts);

    // Check for new unacknowledged alerts and trigger notification
    const active = alerts.filter(a => a.status === "active");
    if (active.length > 0) {
      // Trigger browser notification if permission granted
      notifySupervisor(active[0]);
    }
  }, (error) => {
    console.error("[Safety] Listener error:", error);
    callback([]);
  });
}

/**
 * Subscribe to active-only alerts (unacknowledged)
 */
export function onActiveAlerts(organizationId, callback) {
  const q = query(
    orgCollection("safetyAlerts", organizationId),
    where("status", "==", "active"),
    orderBy("createdAt", "desc")
  );

  return onSnapshot(q, (snapshot) => {
    const alerts = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(alerts);
  }, (error) => {
    console.error("[Safety] Active alerts listener error:", error);
    callback([]);
  });
}


// ===== BROWSER NOTIFICATIONS =====

/**
 * Request notification permission (call on admin dashboard mount)
 */
export async function requestNotificationPermission() {
  if (!("Notification" in window)) return false;
  if (Notification.permission === "granted") return true;
  if (Notification.permission === "denied") return false;

  const permission = await Notification.requestPermission();
  return permission === "granted";
}

/**
 * Send browser notification for a safety alert
 */
export function notifySupervisor(alert) {
  if (!("Notification" in window) || Notification.permission !== "granted") return;

  const title = `🚨 SAFETY ALERT — ${alert.category || "Priority"}`;
  const body = `${alert.agentName || "Agent"} flagged "${alert.triggerPhrase}" during session with ${alert.individualName || "individual"}`;

  try {
    const notification = new Notification(title, {
      body,
      icon: "/favicon.ico",
      tag: `safety-${alert.id || Date.now()}`,
      requireInteraction: true,
      vibrate: [200, 100, 200, 100, 200],
    });

    notification.onclick = () => {
      window.focus();
      notification.close();
    };

    // Auto-close after 30 seconds
    setTimeout(() => {
      try { notification.close(); } catch (e) {}
    }, 30000);
  } catch (e) {
    console.error("[Safety] Notification failed:", e);
  }
}

/**
 * Send browser notification for general events (notes submitted, etc.)
 */
export function sendBrowserNotification(title, body, tag) {
  if (!("Notification" in window) || Notification.permission !== "granted") return;
  try {
    const n = new Notification(title, {
      body,
      icon: "/favicon.ico",
      tag: tag || `fn-${Date.now()}`,
    });
    n.onclick = () => { window.focus(); n.close(); };
    setTimeout(() => { try { n.close(); } catch (e) {} }, 15000);
  } catch (e) {}
}

/**
 * Play alert sound (call from dashboard when new alert arrives)
 */
export function playAlertSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    
    // Three urgent beeps
    [0, 0.2, 0.4].forEach(delay => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 880; // A5 — urgent tone
      gain.gain.value = 0.3;
      osc.start(ctx.currentTime + delay);
      osc.stop(ctx.currentTime + delay + 0.15);
    });
  } catch (e) {
    // Audio context not available
  }
}


// ===== TRIGGER CATEGORIZATION =====

function categorizeTrigger(phrase) {
  const p = (phrase || "").toLowerCase();

  if (p.includes("suicid") || p.includes("kill myself") || p.includes("don't want to be here") ||
      p.includes("end it") || p.includes("self-harm") || p.includes("self harm") ||
      p.includes("hurt myself") || p.includes("not worth living")) {
    return "Suicidal Ideation";
  }

  if (p.includes("homicid") || p.includes("harm to others") || p.includes("kill") ||
      p.includes("hurt someone") || p.includes("want to hurt")) {
    return "Homicidal Ideation";
  }

  if (p.includes("abuse") || p.includes("neglect") || p.includes("hitting me") ||
      p.includes("molest") || p.includes("assault") || p.includes("mandatory report")) {
    return "Abuse/Neglect";
  }

  if (p.includes("using") || p.includes("relapse") || p.includes("started drinking") ||
      p.includes("back on") || p.includes("overdose") || p.includes("substance")) {
    return "Substance Crisis";
  }

  if (p.includes("medication") || p.includes("stopped taking") || p.includes("not taking") ||
      p.includes("missed") || p.includes("ran out")) {
    return "Medication Non-Compliance";
  }

  if (p.includes("evict") || p.includes("homeless") || p.includes("nowhere to go") ||
      p.includes("living in") || p.includes("sleeping in") || p.includes("unsafe")) {
    return "Housing Crisis";
  }

  if (p.includes("crisis") || p.includes("emergency") || p.includes("urgent")) {
    return "Crisis";
  }

  return "Safety Concern";
}

function getSeverity(phrase) {
  const p = (phrase || "").toLowerCase();

  // Critical — immediate danger
  if (p.includes("suicid") || p.includes("homicid") || p.includes("kill") ||
      p.includes("self-harm") || p.includes("self harm") || p.includes("overdose") ||
      p.includes("abuse") || p.includes("mandatory report")) {
    return "critical";
  }

  // High — needs same-day response
  if (p.includes("relapse") || p.includes("substance") || p.includes("evict") ||
      p.includes("homeless") || p.includes("crisis") || p.includes("unsafe")) {
    return "high";
  }

  // Medium — needs follow-up
  return "medium";
}